/**
 * LocalEventTemplate - Renders event pages from local YAML files
 *
 * This template handles events stored in content/events/*.yaml
 */

import { graphql } from "gatsby"
import { getImage, withArtDirection } from "gatsby-plugin-image"
import React, { useState, useEffect } from "react"
import iconCalendar from "../../assets/icons/calendar.svg"
import iconDuration from "../../assets/icons/duration.svg"
import iconLocation from "../../assets/icons/location.svg"
import iconTime from "../../assets/icons/time.svg"
import {
  convertToCustomDateFormat,
  resetTime,
  convertToPDT,
} from "../../lib/functions"
import validateYoutubeUrl from "../../lib/validateYoutubeUrl"
import { useFeatureFlags } from "effects/useSiteContent"
import AuthorAvatars from "components/AuthorAvatars"
import Button from "components/Button"
import HubspotFormEmbed from "components/HubspotFormEmbed"
import YoutubeVideo from "components/YoutubeVideo"
import Img from "components/Img"
import Paper from "components/Paper"
import { SEOHead } from "components/SEO"
import { H4 } from "components/Typography"
import * as css from "./EventPageTemplate.module.css"
import Section from "components/Layout/Section"
import SectionHeader from "components/Layout/SectionHeader"
import Layout from "components/ui/Layout"

/**
 * Format ISO date string to display format (always in Pacific time)
 */
function formatDate(isoString) {
  if (!isoString) return null
  const date = new Date(isoString)
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
    timeZone: "America/Los_Angeles",
  })
}

/**
 * Format ISO date string to time format (always in Pacific time, with PDT/PST label)
 */
function formatTime(isoString) {
  if (!isoString) return null
  const date = new Date(isoString)
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/Los_Angeles",
    timeZoneName: "short",
  })
}

function LocalEventTemplate({ data, location: { pathname } }) {
  const event = data.eventsYaml

  const {
    author,
    category,
    date: rawDate,
    endDate: rawEndDate,
    description,
    duration,
    subtitle,
    hubspotForm,
    title,
    videoUrl: url,
    location: eventLocation,
    slug,
  } = event

  // Format dates
  const date = formatDate(rawDate)
  const time = formatTime(rawDate)
  // endDate and endTime are available if needed for future display
  // const endDate = formatDate(rawEndDate)
  // const endTime = rawEndDate ? formatTime(rawEndDate) : null

  const { enableTutorialsRedirect } = useFeatureFlags()
  const [isFuture, setIsFuture] = useState(false)
  const [localTime, setLocalTime] = useState(null)

  useEffect(() => {
    if (url && !validateYoutubeUrl(url)) window.location.href = url

    if (rawDate) {
      const d = new Date(rawDate)
      const local = d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      const pt = d.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "America/Los_Angeles",
      })
      if (local !== pt) setLocalTime(local)
    }

    if (!url) {
      const eventStartDate = new Date(rawDate)
      const eventEndDate = rawEndDate ? new Date(rawEndDate) : null

      let eventLimitDate = eventEndDate
        ? new Date(eventEndDate)
        : new Date(eventStartDate)
      eventLimitDate.setDate(eventLimitDate.getDate() + 1)
      eventLimitDate = resetTime(eventLimitDate)

      let currentDate = convertToPDT(new Date())
      currentDate = resetTime(currentDate)

      setIsFuture(eventLimitDate > currentDate)
    }
  }, [rawDate, rawEndDate, url])

  const templatePrefix = isFuture ? "future" : "past"

  const {
    [`${templatePrefix}EventHeroDesktop`]: heroDesktop,
    [`${templatePrefix}EventHeroMobile`]: heroMobile,
  } = data

  const heroBackgroundImages = withArtDirection(
    getImage(heroDesktop.childImageSharp),
    [
      {
        image: getImage(heroMobile.childImageSharp),
        media: `(max-width: 768px)`,
      },
    ]
  )

  const formattedDate = rawDate ? convertToCustomDateFormat(rawDate) : null

  const meta = [
    {
      icon: iconCalendar,
      text: formattedDate,
    },
    {
      icon: iconTime,
      text: time || null,
      subtext: localTime ? `${localTime} your time` : null,
    },
    {
      icon: iconLocation,
      text: eventLocation,
    },
    {
      icon: iconDuration,
      text: duration ? `${duration} min` : null,
    },
  ]

  return (
    <Layout
      headerDark
      headerFloating
      pageContentType="landing-page"
      pageName={slug}
      pagePath={pathname}
    >
      {isFuture ? (
        <Section className={css.futureBanner}>
          <SectionHeader
            title={title}
            level="h1"
            className={css.headerSectionTitle}
          />
          <Img
            image={heroBackgroundImages}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: "100%",
              zIndex: -1,
            }}
            withArtDirection
          />
          <div
            className={`grid grid-cols-1 lg:grid-cols-[9fr_6fr] gap-8 ${css.grid}`}
          >
            <div>
              <div className={css.category}>{category}</div>
              <h3 className={css.subtitle}>{subtitle}</h3>
              {description && (
                <div
                  className={`${css.description} prose prose-invert`}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
              {author && (
                <div className={`${css.presenters} ${css.presentersOnDark}`}>
                  <span className={css.presentersLabel}>Presented by</span>
                  <AuthorAvatars author={author} size={28} />
                </div>
              )}
              <div className={css.meta}>
                {meta.map(
                  (item) =>
                    item.text && (
                      <div className={css.metaItem} key={item.text}>
                        <img src={item.icon} alt="Meta Item" />
                        <div>
                          <div>{item.text}</div>
                          {item.subtext && (
                            <div className={css.localTimeHint}>
                              {item.subtext}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                )}
              </div>
            </div>
            {hubspotForm && (
              <div>
                <Paper className={css.hubspot}>
                  <H4>{hubspotForm.formLabel || "Register for the event"}</H4>
                  <HubspotFormEmbed formId={hubspotForm.formId} />
                </Paper>
              </div>
            )}
          </div>
        </Section>
      ) : (
        <>
          <Section className={css.pastHeaderSection}>
            <SectionHeader
              title={title}
              level="h1"
              className={css.headerSectionTitle}
            />
            <Img
              image={heroBackgroundImages}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%",
              }}
              withArtDirection
            />
          </Section>
          <Section>
            <div className="flex justify-center">
              <div className="w-full max-w-3xl">
                <div className={css.category}>{category}</div>
                <div className={css.meta}>
                  <div className={css.location}>{eventLocation}</div>
                  <div className={css.date}>
                    <time dateTime={rawDate}>{date}</time>
                  </div>
                </div>
                {author && (
                  <div className={css.presenters}>
                    <span className={css.presentersLabel}>Presented by</span>
                    <AuthorAvatars author={author} size={28} />
                  </div>
                )}
                {description && (
                  <div
                    className={`${css.description} prose prose-dark-grey`}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}
                {url &&
                  validateYoutubeUrl(url) &&
                  (enableTutorialsRedirect ? (
                    <Button
                      className={css.watchButton}
                      href={`/tutorials/?watch=${slug}`}
                      primary
                    >
                      Watch Recording on Tutorials Page →
                    </Button>
                  ) : (
                    <YoutubeVideo url={url} />
                  ))}
              </div>
            </div>
          </Section>
        </>
      )}
    </Layout>
  )
}

export function Head({ data }) {
  const event = data.eventsYaml

  return (
    <SEOHead
      title={event.title}
      description={event.description?.substring(0, 160)}
      thumbnail={event.thumbnail?.url}
    />
  )
}

export const pageQuery = graphql`
  query LocalEventQuery($slug: String!) {
    eventsYaml(slug: { eq: $slug }) {
      title
      slug
      author
      category
      subtitle
      date
      endDate
      duration
      location
      videoUrl
      description
      thumbnail {
        url
        alt
      }
      hubspotForm {
        formId
        formLabel
      }
    }
    pastEventHeroMobile: file(
      relativePath: { eq: "images/landing-page-hero-bg.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 100, width: 1200, layout: FIXED)
      }
    }
    pastEventHeroDesktop: file(
      relativePath: { eq: "images/landing-page-hero-bg.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 100, width: 3072, layout: FIXED)
      }
    }
    futureEventHeroMobile: file(
      relativePath: { eq: "images/waves-mobile.png" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 100, width: 640, layout: FIXED)
      }
    }
    futureEventHeroDesktop: file(
      relativePath: { eq: "images/waves-desktop.png" }
    ) {
      childImageSharp {
        gatsbyImageData(quality: 100, width: 2240, layout: FIXED)
      }
    }
  }
`

export default LocalEventTemplate
